/**
 * The one list of languages CoLiviq speaks to tenants in (COL-477 / COL-478).
 *
 * Mirrored verbatim in `supabase/functions/_shared/languages.ts` because edge
 * functions cannot import from `src/`. `languages.test.ts` asserts the two
 * copies agree, so a change here that is not copied over fails the suite.
 *
 * Pure data + pure helpers only: no React, no Vite env, no Supabase — that is
 * what lets the edge copy be byte-identical.
 */

export const SUPPORTED_LANGUAGES = [
  "de",
  "en",
  "fr",
  "ar",
  "uk",
  "es",
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** German: the app has always defaulted to it and every legal document is German. */
export const DEFAULT_LANGUAGE: SupportedLanguage = "de";

/**
 * The two languages that existed before COL-477. Always selectable by every
 * account; the other four are offered to a signed-in account on the surfaces
 * that render them (see `tenant-languages.ts`).
 */
export const BASE_LANGUAGES: readonly SupportedLanguage[] = ["de", "en"];

/**
 * Each language named in itself. Deliberately not translated: a picker shown
 * to someone who has not chosen a language yet must be readable whatever the
 * current UI language is.
 */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  de: "Deutsch",
  en: "English",
  fr: "Français",
  ar: "العربية",
  uk: "Українська",
  es: "Español",
};

/**
 * BCP 47 locale used for Intl date / number formatting per language.
 *
 * Arabic uses Western digits (`nu-latn`) on purpose: euro amounts and due
 * dates must read identically for the tenant, the admin and the bank, and
 * Eastern Arabic numerals in a rent reminder invite transcription mistakes.
 */
const LOCALES: Record<SupportedLanguage, string> = {
  de: "de-DE",
  en: "en-GB",
  fr: "fr-FR",
  ar: "ar-u-nu-latn",
  uk: "uk-UA",
  es: "es-ES",
};

export function isSupportedLanguage(
  value: unknown
): value is SupportedLanguage {
  return (
    typeof value === "string" &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * Narrow any stored / user-supplied value to a supported language.
 * Anything unknown (NULL, "", "xx", "EN" with the wrong case…) becomes the
 * fallback, which defaults to German. Case-insensitive on purpose: DocuSign
 * and some browsers report "EN" / "de-DE" style tags.
 */
export function coerceLanguage(
  value: unknown,
  fallback: SupportedLanguage = DEFAULT_LANGUAGE
): SupportedLanguage {
  if (typeof value !== "string") return fallback;
  const base = value.trim().toLowerCase().split(/[-_]/)[0];
  return isSupportedLanguage(base) ? base : fallback;
}

export function localeFor(language: SupportedLanguage): string {
  return LOCALES[language];
}

export type TextDirection = "ltr" | "rtl";

export function dirFor(language: SupportedLanguage): TextDirection {
  return language === "ar" ? "rtl" : "ltr";
}

/** The two languages every pre-COL-477 surface was written in. */
export type BaseLanguage = "de" | "en";

/**
 * Collapse to the copy that exists today. Surfaces not yet swept into the
 * translation table (phase 1, COL-479) carry inline German/English
 * dictionaries; a French, Arabic, Ukrainian or Spanish tenant sees their
 * English half, mirroring the t() fallback chain (active → en → de).
 */
export function baseLanguageFor(language: SupportedLanguage): BaseLanguage {
  return language === "de" ? "de" : "en";
}
